import React, { useEffect, useRef } from 'react'
import { Toolbar } from './components/Toolbar'
import { RTLSPanel } from './components/RTLSPanel'
import { SceneCanvas } from './scene/Canvas'
import { useBootstrapMap } from './scene/hooks/useBootstrapMap'
import { SceneRefContext, type SceneRef } from './scene/SceneRefContext'
import { useAppDispatch } from './app/useAppDispatch'
import { useAppSelector } from './app/useAppSelector'
import { startRTLSStream, stopRTLSStream } from './features/rtls/rtlsService'
import { ToastHost, showToast } from './components/Toast'
import { PlaybackBar } from './components/playback/PlaybackBar'
import { AuthProvider, useAuth } from './app/useAuth'
import { LoginScreen } from './screens/auth/LoginScreen'

// AuthGate shows the LoginScreen until a session is established. Wraps the
// full RTLS app so unauthenticated users never see the 3D scene / Redux
// state hydrate.
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, loading } = useAuth()
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background text-textMuted">
        Loading…
      </div>
    )
  }
  if (!session) {
    return <LoginScreen />
  }
  return <>{children}</>
}

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <AuthGate>
        <AppInner />
      </AuthGate>
    </AuthProvider>
  )
}

const AppInner: React.FC = () => {
  const sceneRefHolder = useRef<SceneRef | null>(null)
  const { loading, error } = useBootstrapMap()
  const dispatch = useAppDispatch()
  const mineId = useAppSelector(s => s.map.mineId)
  const playbackMode = useAppSelector(s => s.rtls.playbackMode)
  const tappedCoord = useAppSelector(s => s.ui.tappedCoord)

  // Show a toast with the tapped coordinates whenever MapTapHandler dispatches
  // a new tappedCoord. Transitions to null (e.g. AddLocationDialog clearing
  // after read) are intentionally ignored so the UX doesn't flash an empty toast.
  useEffect(() => {
    if (!tappedCoord) return
    showToast(
      `X: ${tappedCoord.x.toFixed(1)}, Y: ${tappedCoord.y.toFixed(1)}, Z: ${tappedCoord.z.toFixed(1)}`,
      'info',
    )
  }, [tappedCoord])

  // Re-subscribe whenever mineId changes. In JSON/offline mode mineId stays
  // null and startRTLSStream uses the fixture path; in Supabase mode, this
  // effect defers subscription until useBootstrapMap has resolved the mine.
  useEffect(() => {
    startRTLSStream(dispatch, mineId)
    return () => stopRTLSStream()
  }, [dispatch, mineId])

  return (
    <SceneRefContext.Provider value={sceneRefHolder}>
      <div className="flex h-full w-full overflow-hidden bg-background">
        <Toolbar />
        <main
          className="relative flex flex-1 overflow-hidden bg-background"
          aria-label="3D mine map canvas"
        >
          {loading ? (
            <div className="flex h-full w-full items-center justify-center">
              <span className="text-sm text-textMuted">
                Loading map configuration…
              </span>
            </div>
          ) : (
            <SceneCanvas sceneRefHolder={sceneRefHolder} />
          )}
          {error && !loading && (
            <div className="absolute bottom-sm left-sm rounded-md border border-alertBorder bg-background/80 px-md py-xs text-xs text-statusRed shadow">
              Map config warning: {error} — using defaults.
            </div>
          )}
          {playbackMode === 'playback' && <PlaybackBar />}
        </main>
        <RTLSPanel />
        <ToastHost />
      </div>
    </SceneRefContext.Provider>
  )
}
